<template>
  <div class="glass-theme fade-in-page">
    <!-- Enhanced Custom Loader Overlay with Glass Effect & Fixed Height -->
    <transition name="fade-loader">
      <div v-if="loading" class="custom-loader-overlay">
        <div class="loader-card glass-loader-card">
          <div class="loader-spinner-wrapper">
            <div class="glass-spinner-ring">
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="ring"></div>
            </div>
          </div>
          <div class="loader-dots">
            <span></span><span></span><span></span><span></span>
          </div>
          <p class="loader-text mt-3 mb-0">Loading drafts <span class="loader-ellipsis">...</span></p>
          <div class="loading-progress mt-3">
            <div class="progress-bar" :style="{ width: loadingProgress + '%' }"></div>
          </div>
        </div>
      </div>
    </transition>

    <div style="padding: 15px;" class="responsive-container">
      <!-- Animated Title -->
      <div class="title-section mb-4">
        <h3 class="responsive-title">
          <i class="fas fa-file-invoice-dollar title-icon"></i>
          DRAFT INVOICES (Unsubmitted)
          <transition name="badge-pop">
            <span v-if="pagination.total > 0" class="badge badge-pill badge-secondary count-badge">{{ pagination.total }}</span>
          </transition>
        </h3>
        <div class="title-underline"></div>
      </div>
      
      <!-- Search/Filter Bar - with glass styling -->
      <b-row class="mb-4 filter-row">
        <b-col md="4" sm="12" class="mb-2 mb-md-0">
          <b-input-group size="sm" class="search-group">
            <b-form-input
              v-model="filters.search"
              placeholder="Search by invoice ID, customer name, email..."
              @keyup.enter="loadDrafts"
              class="search-input glass-input"
            ></b-form-input>
            <b-input-group-append>
              <b-button variant="primary" @click="loadDrafts" class="search-btn glass-btn">
                <b-icon-search></b-icon-search>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-col>
        <b-col md="3" sm="6" class="mb-2 mb-md-0">
          <b-form-select
            v-model="filters.location"
            :options="locationOptions"
            size="sm"
            @change="loadDrafts"
            class="animated-select glass-select"
          >
            <template #first>
              <b-form-select-option :value="null">All Locations</b-form-select-option>
            </template>
          </b-form-select>
        </b-col>
        <b-col md="3" sm="6" class="mb-2 mb-md-0">
          <b-form-select
            v-model="filters.dateRange"
            :options="dateRangeOptions"
            size="sm"
            @change="loadDrafts"
            class="animated-select glass-select"
          ></b-form-select>
        </b-col>
        <b-col md="2" sm="12" class="text-right">
          <b-button size="sm" variant="success" @click="refresh" class="responsive-btn refresh-btn glass-btn" block>
            <b-icon-arrow-repeat class="refresh-icon"></b-icon-arrow-repeat> Refresh
          </b-button>
        </b-col>
      </b-row>

      <!-- Animated Empty State -->
      <transition name="fade-up">
        <div v-if="drafts.length === 0 && !loading" class="text-center p-5 empty-state glass-card">
          <div class="empty-icon-wrapper">
            <b-icon-file-text font-scale="4" variant="secondary" class="empty-icon"></b-icon-file-text>
          </div>
          <h5 class="mt-3 empty-title">No draft invoices found</h5>
          <p class="text-muted empty-subtitle">Create a new sale and save as draft</p>
          <b-button variant="primary" size="sm" class="mt-2 create-btn glass-btn">
            <i class="fas fa-plus"></i> Create New Sale
          </b-button>
        </div>
      </transition>

      <!-- Desktop Table View with Glass Styling -->
      <div class="d-none d-md-block">
        <b-table
          :items="drafts"
          :fields="fields"
          striped
          hover
          small
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          show-empty
          responsive
          class="animated-table glass-table"
        >
          <template #cell(created_at)="data">
            <span class="date-cell">{{ formatDate(data.value) }}</span>
          </template>

          <template #cell(last_modified)="data">
            <span class="date-cell">{{ formatDate(data.value) }}</span>
          </template>

          <template #cell(customer)="data">
            <div v-if="data.item.customer && Object.keys(data.item.customer).length > 0" class="customer-cell">
              <strong class="customer-name">
                <i class="fas fa-user-circle"></i> {{ data.item.customer.fullname || 'No name' }}
              </strong>
              <br>
              <small class="customer-email">{{ data.item.customer.email || '' }}</small>
              <br>
              <small class="customer-phone">{{ data.item.customer.phone || '' }}</small>
              
              <div class="mt-1 badge-group">
                <b-badge 
                  :variant="(data.item.data && data.item.data.terms_agreed) ? 'success' : 'warning'"
                  pill
                  class="animated-badge mr-1 mb-1 glass-badge"
                >
                  <b-icon-check-circle v-if="data.item.data && data.item.data.terms_agreed" class="badge-icon"></b-icon-check-circle>
                  <b-icon-exclamation-circle v-else class="badge-icon"></b-icon-exclamation-circle>
                  Terms
                </b-badge>
                
                <b-badge 
                  v-if="data.item.data && data.item.data.warranty_refused"
                  variant="info"
                  pill
                  class="animated-badge mr-1 mb-1 glass-badge"
                >
                  <i class="fas fa-shield-alt"></i> Warranty Refused
                </b-badge>
                
                <b-badge 
                  v-if="data.item.data && data.item.data.delivery_waiver_agreed"
                  variant="secondary"
                  pill
                  class="animated-badge mr-1 mb-1 glass-badge"
                >
                  <i class="fas fa-truck"></i> Delivery Waiver
                </b-badge>
                
                <b-badge 
                  v-if="data.item.data && data.item.data.space_preparation_agreed"
                  variant="dark"
                  pill
                  class="animated-badge mr-1 mb-1 glass-badge"
                >
                  <i class="fas fa-home"></i> Space Prep
                </b-badge>
                
                <b-badge 
                  v-if="data.item.data && data.item.data.has_signature"
                  variant="info"
                  pill
                  class="animated-badge mb-1 glass-badge"
                >
                  <b-icon-pencil class="badge-icon"></b-icon-pencil> Signed
                </b-badge>
              </div>
            </div>
            <span v-else class="text-muted">No customer info</span>
          </template>

          <template #cell(items_count)="data">
            <b-badge variant="info" pill class="items-badge glass-badge">
              <i class="fas fa-boxes"></i> {{ data.value }} items
            </b-badge>
          </template>

          <template #cell(total)="data">
            <span class="total-amount">${{ Number(data.value).toFixed(2) }}</span>
          </template>

          <template #cell(actions)="data">
            <b-dropdown size="sm" variant="primary" text="Actions" right class="action-dropdown glass-dropdown" toggle-class="action-toggle">
              <template #button-content>
                <i class="fas fa-cog"></i> Actions <i class="fas fa-chevron-down ml-1"></i>
              </template>
              <b-dropdown-item @click="continueEditing(data.item)" class="animated-dropdown-item">
                <b-icon-pencil class="mr-2"></b-icon-pencil> Continue Editing
              </b-dropdown-item>
              
              <b-dropdown-item 
                @click="openCustomerInfoTab(data.item)"
                class="animated-dropdown-item"
              >
                <b-icon-box-arrow-up-right class="mr-2"></b-icon-box-arrow-up-right> Customer Info & Agreement
              </b-dropdown-item>
              
              <b-dropdown-item @click="viewDraft(data.item)" class="animated-dropdown-item">
                <b-icon-eye class="mr-2"></b-icon-eye> View Summary
              </b-dropdown-item>
              
              <b-dropdown-item 
                @click="viewEstimatedInvoice(data.item)"
                class="animated-dropdown-item"
              >
                <b-icon-file-text class="mr-2"></b-icon-file-text> View Estimated Invoice
              </b-dropdown-item>
              
              <b-dropdown-divider></b-dropdown-divider>
              <b-dropdown-item 
                variant="danger" 
                @click="deleteDraft(data.item)"
                class="animated-dropdown-item text-danger"
              >
                <b-icon-trash class="mr-2"></b-icon-trash> Delete Draft
              </b-dropdown-item>
            </b-dropdown>
          </template>

          <template #cell(progress)="data">
            <div class="progress-wrapper">
              <b-progress 
                :value="getProgressPercentage(data.item)" 
                max="100" 
                height="8px"
                :variant="getProgressVariant(data.item)"
                show-progress
                class="animated-progress glass-progress"
              ></b-progress>
              <small class="progress-text">{{ getProgressText(data.item) }}</small>
            </div>
          </template>
        </b-table>
      </div>

      <!-- Mobile Card View with Glass Styling -->
      <div class="d-block d-md-none">
        <transition-group name="card-list" tag="div">
          <div v-for="(draft, index) in drafts" :key="draft.draft_id" class="mobile-card glass-mobile-card mb-3" :style="{ animationDelay: (index * 0.05) + 's' }">
            <div class="card-header d-flex justify-content-between align-items-start">
              <div>
                <strong class="draft-id">
                  <i class="fas fa-receipt"></i> {{ draft.draft_id }}
                </strong>
                <div class="text-muted small">
                  <i class="far fa-calendar-alt"></i> {{ formatDate(draft.created_at) }}
                </div>
              </div>
              <b-dropdown size="sm" variant="outline-primary" text="Actions" right no-caret class="mobile-dropdown glass-dropdown">
                <template #button-content>
                  <i class="fas fa-ellipsis-v"></i>
                </template>
                <b-dropdown-item @click="continueEditing(draft)" class="animated-dropdown-item">
                  <b-icon-pencil class="mr-2"></b-icon-pencil> Continue Editing
                </b-dropdown-item>
                <b-dropdown-item @click="openCustomerInfoTab(draft)" class="animated-dropdown-item">
                  <b-icon-box-arrow-up-right class="mr-2"></b-icon-box-arrow-up-right> Customer Info
                </b-dropdown-item>
                <b-dropdown-item @click="viewDraft(draft)" class="animated-dropdown-item">
                  <b-icon-eye class="mr-2"></b-icon-eye> View Summary
                </b-dropdown-item>
                <b-dropdown-item @click="viewEstimatedInvoice(draft)" class="animated-dropdown-item">
                  <b-icon-file-text class="mr-2"></b-icon-file-text> View Invoice
                </b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item variant="danger" @click="deleteDraft(draft)" class="animated-dropdown-item text-danger">
                  <b-icon-trash class="mr-2"></b-icon-trash> Delete
                </b-dropdown-item>
              </b-dropdown>
            </div>
            
            <div class="card-body">
              <div class="info-row mb-2">
                <i class="fas fa-user-tie"></i>
                <span class="info-label">Created By:</span>
                <span class="info-value">{{ draft.username }}</span>
              </div>
              
              <div class="info-row mb-2">
                <i class="fas fa-map-marker-alt"></i>
                <span class="info-label">Location:</span>
                <span class="info-value">{{ draft.location }}</span>
              </div>
              
              <div class="info-row mb-2">
                <i class="fas fa-boxes"></i>
                <span class="info-label">Items:</span>
                <b-badge variant="info" pill class="items-badge-mobile glass-badge">{{ draft.items_count }} items</b-badge>
              </div>
              
              <div class="customer-info mb-2" v-if="draft.customer && Object.keys(draft.customer).length > 0">
                <i class="fas fa-user-circle"></i>
                <div class="customer-details">
                  <strong>{{ draft.customer.fullname || 'No name' }}</strong>
                  <div class="small text-muted">{{ draft.customer.email || '' }}</div>
                  <div class="small text-muted">{{ draft.customer.phone || '' }}</div>
                </div>
              </div>
              
              <div class="agreement-badges mb-2">
                <b-badge 
                  :variant="(draft.data && draft.data.terms_agreed) ? 'success' : 'warning'"
                  pill
                  class="animated-badge-mobile mr-1 mb-1 glass-badge"
                >
                  <i :class="(draft.data && draft.data.terms_agreed) ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i> Terms
                </b-badge>
                
                <b-badge 
                  v-if="draft.data && draft.data.warranty_refused"
                  variant="info"
                  pill
                  class="animated-badge-mobile mr-1 mb-1 glass-badge"
                >
                  <i class="fas fa-shield-alt"></i> Warranty Refused
                </b-badge>
                
                <b-badge 
                  v-if="draft.data && draft.data.has_signature"
                  variant="info"
                  pill
                  class="animated-badge-mobile mb-1 glass-badge"
                >
                  <i class="fas fa-pen"></i> Signed
                </b-badge>
              </div>
              
              <div class="progress-section">
                <b-progress 
                  :value="getProgressPercentage(draft)" 
                  max="100" 
                  height="6px"
                  :variant="getProgressVariant(draft)"
                  class="animated-progress-mobile mb-1 glass-progress"
                ></b-progress>
                <small class="progress-text-mobile">{{ getProgressText(draft) }}</small>
              </div>
            </div>
          </div>
        </transition-group>
      </div>

      <!-- Animated Pagination -->
      <transition name="fade-up">
        <b-row v-if="drafts.length > 0" class="mt-4">
          <b-col md="6" sm="12" class="mb-2 mb-md-0">
            <b-pagination
              v-model="pagination.page"
              :total-rows="pagination.total"
              :per-page="pagination.perPage"
              @change="loadDrafts"
              size="sm"
              :hide-goto-end-buttons="isMobile"
              align="center"
              class="animated-pagination glass-pagination"
            ></b-pagination>
          </b-col>
          <b-col md="6" sm="12" class="text-md-right text-center text-muted">
            <small class="pagination-info">
              <i class="fas fa-chart-line"></i> Showing {{ drafts.length }} of {{ pagination.total }} drafts
            </small>
          </b-col>
        </b-row>
      </transition>
    </div>

    <!-- Estimated Invoice Preview Modal -->
    <b-modal 
      id="modal-estimated-invoice-preview"
      size="xl"
      title="Estimated Invoice Preview"
      hide-footer
      scrollable
      centered
      modal-class="estimated-invoice-modal glass-modal"
      body-class="p-0"
      header-class="bg-warning text-dark"
    >
      <template #modal-header>
        <div class="d-flex align-items-center">
          <b-icon-exclamation-triangle class="mr-2" variant="dark" scale="1.5"></b-icon-exclamation-triangle>
          <h5 class="mb-0 text-dark font-weight-bold">ESTIMATED INVOICE PREVIEW - NOT A BILL</h5>
        </div>
      </template>
      
      <transition name="fade">
        <div v-if="selectedDraft" class="agreement-summary p-3 bg-light border-bottom">
          <h6 class="mb-2"><i class="fas fa-handshake"></i> Agreement Status</h6>
          <div class="responsive-agreement-grid">
            <div class="agreement-item">
              <b-badge :variant="(selectedDraft.data && selectedDraft.data.terms_agreed) ? 'success' : 'warning'" pill class="p-2 glass-badge">
                <b-icon-check-circle v-if="selectedDraft.data && selectedDraft.data.terms_agreed" class="mr-1"></b-icon-check-circle>
                <b-icon-exclamation-circle v-else class="mr-1"></b-icon-exclamation-circle>
                Terms & Conditions
              </b-badge>
              <small class="d-block text-muted mt-1">
                {{ (selectedDraft.data && selectedDraft.data.terms_agreed) ? 'Agreed' : 'Not agreed' }}
              </small>
            </div>
            
            <div class="agreement-item">
              <b-badge :variant="(selectedDraft.data && selectedDraft.data.warranty_refused) ? 'info' : 'secondary'" pill class="p-2 glass-badge">
                <i class="fas fa-ban mr-1"></i> Warranty Refused
              </b-badge>
              <small class="d-block text-muted mt-1">
                {{ (selectedDraft.data && selectedDraft.data.warranty_refused) ? 'Yes - Refused' : 'Not refused' }}
              </small>
            </div>
            
            <div class="agreement-item">
              <b-badge :variant="(selectedDraft.data && selectedDraft.data.delivery_waiver_agreed) ? 'info' : 'secondary'" pill class="p-2 glass-badge">
                <i class="fas fa-truck-fast mr-1"></i> Delivery Waiver
              </b-badge>
              <small class="d-block text-muted mt-1">
                {{ (selectedDraft.data && selectedDraft.data.delivery_waiver_agreed) ? 'Waived White Glove' : 'Standard Delivery' }}
              </small>
            </div>
            
            <div class="agreement-item">
              <b-badge :variant="(selectedDraft.data && selectedDraft.data.space_preparation_agreed) ? 'info' : 'secondary'" pill class="p-2 glass-badge">
                <i class="fas fa-home mr-1"></i> Space Prep
              </b-badge>
              <small class="d-block text-muted mt-1">
                {{ (selectedDraft.data && selectedDraft.data.space_preparation_agreed) ? 'Acknowledged' : 'Not acknowledged' }}
              </small>
            </div>
          </div>
          
          <div class="mt-3 pt-2 border-top">
            <b-badge :variant="(selectedDraft.data && selectedDraft.data.has_signature) ? 'success' : 'danger'" pill class="p-2 glass-badge">
              <i class="fas fa-signature mr-1"></i>
              {{ (selectedDraft.data && selectedDraft.data.has_signature) ? 'Customer Signed' : 'No Signature' }}
            </b-badge>
            <small v-if="selectedDraft.data && selectedDraft.data.signature_captured_at" class="d-block text-muted mt-1">
              Signed: {{ formatDate(selectedDraft.data.signature_captured_at) }}
            </small>
          </div>
        </div>
      </transition>
      
      <EstimatedInvoicePreview 
        v-if="selectedDraft"
        :invoiceData="prepareInvoiceData(selectedDraft)"
        :locationData="getLocationDetails(selectedDraft.location_id)"
        :policyText="policyText"
        :agreementData="getAgreementData(selectedDraft)"
        @close="$bvModal.hide('modal-estimated-invoice-preview')"
        :key="modalKey"
      />
      
      <div v-else class="text-center p-5">
        <b-spinner variant="primary" label="Loading..."></b-spinner>
        <p class="mt-3">Loading invoice data...</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'
import Requestor from '@/utilities/Requestor'
import EstimatedInvoicePreview from '@/components/Sales/EstimatedInvoicePreview.vue'

export default {
  name: 'ListDraftInvoices',
  components: {
    EstimatedInvoicePreview
  },
  data() {
    return {
      loading: false,
      drafts: [],
      sortBy: 'last_modified',
      sortDesc: true,
      loadingProgress: 0,
      allowed_states: [
        {'value': 'PA', 'text': 'PENNSYLVANIA'}, 
        {'value': 'NJ', 'text': 'NEW JERSEY'}, 
        {'value': 'DE', 'text': 'DELAWARE'},
        {'value': 'AL', 'text': 'ALABAMA'}, 
        {'value': 'AK', 'text': 'ALASKA'}, 
        {'value': 'AS', 'text': 'AMERICAN SAMOA'},
        {'value': 'AZ', 'text': 'ARIZONA'}, 
        {'value': 'AR', 'text': 'ARKANSAS'}, 
        {'value': 'CA', 'text': 'CALIFORNIA'},
        {'value': 'CO', 'text': 'COLORADO'}, 
        {'value': 'CT', 'text': 'CONNECTICUT'}, 
        {'value': 'DC', 'text': 'DISTRICT OF COLUMBIA'},
        {'value': 'FM', 'text': 'FEDERATED STATES OF MICRONESIA'}, 
        {'value': 'FL', 'text': 'FLORIDA'}, 
        {'value': 'GA', 'text': 'GEORGIA'},
        {'value': 'GU', 'text': 'GUAM'}, 
        {'value': 'HI', 'text': 'HAWAII'}, 
        {'value': 'ID', 'text': 'IDAHO'},
        {'value': 'IL', 'text': 'ILLINOIS'}, 
        {'value': 'IN', 'text': 'INDIANA'}, 
        {'value': 'IA', 'text': 'IOWA'},
        {'value': 'KS', 'text': 'KANSAS'}, 
        {'value': 'KY', 'text': 'KENTUCKY'}, 
        {'value': 'LA', 'text': 'LOUISIANA'},
        {'value': 'ME', 'text': 'MAINE'}, 
        {'value': 'MH', 'text': 'MARSHALL ISLANDS'}, 
        {'value': 'MD', 'text': 'MARYLAND'},
        {'value': 'MA', 'text': 'MASSACHUSETTS'}, 
        {'value': 'MI', 'text': 'MICHIGAN'}, 
        {'value': 'MS', 'text': 'MISSISSIPPI'},
        {'value': 'MO', 'text': 'MISSOURI'}, 
        {'value': 'MT', 'text': 'MONTANA'}, 
        {'value': 'NE', 'text': 'NEBRASKA'},
        {'value': 'NV', 'text': 'NEVADA'}, 
        {'value': 'NH', 'text': 'NEW HAMPSHIRE'}, 
        {'value': 'NM', 'text': 'NEW MEXICO'},
        {'value': 'NY', 'text': 'NEW YORK'}, 
        {'value': 'NC', 'text': 'NORTH CAROLINA'}, 
        {'value': 'ND', 'text': 'NORTH DAKOTA'},
        {'value': 'MP', 'text': 'NORTHERN MARIANA ISLANDS'}, 
        {'value': 'OH', 'text': 'OHIO'},
        {'value': 'OK', 'text': 'OKLAHOMA'}, 
        {'value': 'OR', 'text': 'OREGON'}, 
        {'value': 'PW', 'text': 'PALAU'},
        {'value': 'PR', 'text': 'PUERTO RICO'}, 
        {'value': 'RI', 'text': 'RHODE ISLAND'}, 
        {'value': 'SC', 'text': 'SOUTH CAROLINA'},
        {'value': 'SD', 'text': 'SOUTH DAKOTA'}, 
        {'value': 'TN', 'text': 'TENNESSEE'}, 
        {'value': 'TX', 'text': 'TEXAS'},
        {'value': 'UT', 'text': 'UTAH'}, 
        {'value': 'VT', 'text': 'VERMONT'}, 
        {'value': 'VA', 'text': 'VIRGINIA'}, 
        {'value': 'WA', 'text': 'WASHINGTON'}, 
        {'value': 'WV', 'text': 'WEST VIRGINIA'},
        {'value': 'WI', 'text': 'WISCONSIN'}, 
        {'value': 'WY', 'text': 'WYOMING'}
      ],
      zipcodes: [],
      policyText: '',
      policyTextSpanish: '',
      filters: {
        search: '',
        location: null,
        dateRange: 'today'
      },
      locationOptions: [],
      dateRangeOptions: [
        { value: 'today', text: 'Today' },
        { value: 'yesterday', text: 'Yesterday' },
        { value: 'week', text: 'This Week' },
        { value: 'month', text: 'This Month' },
        { value: 'all', text: 'All Time' }
      ],
      fields: [
        { key: 'draft_id', label: 'Draft ID', sortable: true, class: 'text-nowrap' },
        { key: 'username', label: 'Created By', sortable: true, class: 'text-nowrap' },
        { key: 'created_at', label: 'Created', sortable: true, class: 'text-nowrap' },
        { key: 'last_modified', label: 'Last Modified', sortable: true, class: 'text-nowrap' },
        { key: 'location', label: 'Location', sortable: true, class: 'text-nowrap' },
        { key: 'customer', label: 'Customer' },
        { key: 'items_count', label: 'Items', sortable: true, class: 'text-center' },
        { key: 'agreement_status', label: 'Agreement', sortable: true },
        { key: 'progress', label: 'Progress' },
        { key: 'actions', label: 'Actions', class: 'text-center text-nowrap' }
      ],
      pagination: {
        page: 1,
        perPage: 20,
        total: 0
      },
      packet: {},
      selectedDraft: null,
      modalKey: 0,
      locationDetailsCache: {},
      isMobile: false,
      loadingInterval: null,
      msgInterval: null
    }
  },
  computed: {
    userDetails() {
      return JSON.parse(localStorage.userDetails || '{}')
    }
  },
  methods: {
    startLoadingAnimation() {
      this.loadingProgress = 0;
      this.loadingInterval = setInterval(() => {
        if (this.loadingProgress < 90) {
          this.loadingProgress += Math.random() * 10;
          if (this.loadingProgress > 90) this.loadingProgress = 90;
        }
      }, 300);
    },
    stopLoadingAnimation(success = true) {
      if (this.loadingInterval) clearInterval(this.loadingInterval);
      if (success) {
        this.loadingProgress = 100;
        setTimeout(() => {
          this.loading = false;
        }, 500);
      } else {
        this.loading = false;
      }
    },
    checkMobile() {
      this.isMobile = window.innerWidth < 768
    },
    
    openCustomerInfoTab(draft) {
      const baseUrl = window.location.origin
      const route = '/sales/draft-customer-info'
      const url = `${baseUrl}/#${route}?draftId=${draft.draft_id}&t=${Date.now()}`
      
      console.log('Opening URL:', url)
      
      const newWindow = window.open(url, '_blank')
      
      if (!newWindow) {
        this.$bvToast.toast('Popup blocked! Please allow popups for this site.', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 5000
        })
        return
      }
      
      this.$bvToast.toast('Opening customer information in new tab...', {
        title: 'Info',
        variant: 'info',
        autoHideDelay: 2000
      })
    },

    loadZipcodes() {
      let path = "/api/sales/get_all_zipcodes"
      let data = {}
      let that = this
      
      let s = function(response) {
        if (response.data.result) {
          that.zipcodes = response.data.result
        }
      }
      
      let f = function(error) {
        console.error("Error loading zipcodes:", error)
      }
      
      Requestor.getAsyncP(path, data, this, s, f)
    },

    fetchPolicy() {
      let that = this
      
      let pathEn = "/api/policy/get_policy/"
      let dataEn = {language: 'en'}
      
      let sEn = function(response) {
        if (response.data.result && response.data.result.length > 0) {
          that.policyText = response.data.result[0].text
        } else {
          that.policyText = '<p>Please read and agree to our standard terms and conditions.</p>'
        }
      }
      
      let pathEs = "/api/policy/get_policy/"
      let dataEs = {language: 'es'}
      
      let sEs = function(response) {
        if (response.data.result && response.data.result.length > 0) {
          that.policyTextSpanish = response.data.result[0].text
        } else {
          that.policyTextSpanish = '<p>Por favor, lea y acepte nuestros términos y condiciones estándar.</p>'
        }
      }
      
      let f = function(error) {
        console.error("Error loading policy:", error)
        that.policyText = '<p>Please read and agree to our standard terms and conditions.</p>'
        that.policyTextSpanish = '<p>Por favor, lea y acepte nuestros términos y condiciones estándar.</p>'
      }
      
      Requestor.getAsyncP(pathEn, dataEn, this, sEn, f)
      Requestor.getAsyncP(pathEs, dataEs, this, sEs, f)
    },

    formatDate(date) {
      return date ? moment(date).format('MM/DD/YYYY hh:mm A') : 'N/A'
    },
    
    getProgressPercentage(draft) {
      const fields = ['customer', 'items', 'charges', 'payment', 'agreement']
      let filled = 0
      
      const draftData = draft.data || {}
      
      if (draftData.customer && Object.keys(draftData.customer).length > 0) filled++
      if (draftData.items && draftData.items.length > 0) filled++
      if ((draftData.delivery_charges && draftData.delivery_charges > 0) || 
          (draftData.setup_charges && draftData.setup_charges > 0) || 
          (draftData.warranty_charges && draftData.warranty_charges > 0)) filled++
      if (draftData.payment_methods && draftData.payment_methods.length > 0) filled++
      
      let agreementCount = 0
      if (draftData.terms_agreed === true) agreementCount++
      if (draftData.has_signature === true) agreementCount++
      if (draftData.warranty_refused !== undefined) agreementCount++
      if (draftData.delivery_waiver_agreed !== undefined) agreementCount++
      if (draftData.space_preparation_agreed !== undefined) agreementCount++
      
      if (agreementCount >= 3) filled++
      
      return (filled / fields.length) * 100
    },
    
    getProgressVariant(draft) {
      const progress = this.getProgressPercentage(draft)
      if (progress >= 75) return 'success'
      if (progress >= 50) return 'info'
      if (progress >= 25) return 'warning'
      return 'danger'
    },
    
    getProgressText(draft) {
      const progress = this.getProgressPercentage(draft)
      if (progress >= 75) return 'Almost complete'
      if (progress >= 50) return 'Halfway there'
      if (progress >= 25) return 'Started'
      return 'Just started'
    },
    
    loadDrafts() {
      this.loading = true
      this.startLoadingAnimation()
      let path = '/api/sales/list_draft_invoices/'
      let data = {
        ...this.filters,
        page: this.pagination.page,
        per_page: this.pagination.perPage,
        username: this.userDetails.username,
        location_id: this.userDetails.properties.location_id || null,
        is_admin: this.userDetails.is_admin || false
      }
      
      let that = this
      let s = function(response) {
        if (response.data.error) {
          that.$bvToast.toast(response.data.error, {
            title: 'Error',
            variant: 'warning'
          })
          that.drafts = []
        } else if (response.data.result) {
          that.drafts = response.data.result.drafts || []
          that.pagination.total = response.data.result.total || 0
        }
        that.stopLoadingAnimation(true)
      }
      
      let f = function(error) {
        console.error('Error loading drafts:', error)
        that.$bvToast.toast('Error loading drafts', {
          title: 'Error',
          variant: 'danger'
        })
        that.stopLoadingAnimation(false)
        that.drafts = []
      }
      
      Requestor.postSyncP(path, data, this, s, f)
    },
    
    continueEditing(draft) {
      this.$router.push({
        name: 'NewSale',
        query: { draft_id: draft.draft_id }
      }).catch(err => {
        console.error('Navigation error:', err)
        this.$router.push({
          path: '/sales/new/',
          query: { draft_id: draft.draft_id }
        })
      })
    },
    
    viewDraft(draft) {
      this.$bvModal.msgBoxOk(this.formatDraftSummary(draft), {
        title: 'Draft Summary',
        size: 'lg',
        okTitle: 'Close',
        scrollable: true
      })
    },
    
    formatDraftSummary(draft) {
      const data = draft.data || {}
      const customer = data.customer || {}
      const items = data.items || []
      
      let agreementStatus = ''
      if (data.terms_agreed) agreementStatus += '✓ Terms Agreed '
      if (data.warranty_refused) agreementStatus += '| Warranty Refused '
      if (data.delivery_waiver_agreed) agreementStatus += '| Delivery Waiver '
      if (data.space_preparation_agreed) agreementStatus += '| Space Prep Acknowledged '
      if (data.address_confirmation_agreed) agreementStatus += '| ✓ Address Confirmed '
      
      if (data.has_signature) agreementStatus += '| Signed '
      
      let summary = `
        <div style="font-family: monospace; padding: 10px;">
          <h4>Draft Invoice: ${draft.draft_id}</h4>
          <hr>
          <h5>Customer Information</h5>
          <p>
            <strong>Name:</strong> ${customer.fullname || 'N/A'}<br>
            <strong>Email:</strong> ${customer.email || 'N/A'}<br>
            <strong>Phone:</strong> ${customer.phone || 'N/A'}<br>
            <strong>Address:</strong> ${customer.address_line_1 || ''} ${customer.city || ''} ${customer.state || ''} ${customer.zipcode || ''}
          </p>
          <hr>
          <h5>Agreement Status</h5>
          <p>${agreementStatus || 'No agreements recorded'}</p>
          <hr>
          <h5>Items (${items.length})</h5>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background: #f0f0f0;">
                <th style="padding: 5px; border: 1px solid #ddd;">SKU</th>
                <th style="padding: 5px; border: 1px solid #ddd;">Product</th>
                <th style="padding: 5px; border: 1px solid #ddd;">Qty</th>
                <th style="padding: 5px; border: 1px solid #ddd;">Price</th>
              </tr>
            </thead>
            <tbody>
      `
      
      items.forEach(item => {
        summary += `
              <tr>
                <td style="padding: 5px; border: 1px solid #ddd;">${item.product_sku || ''}</td>
                <td style="padding: 5px; border: 1px solid #ddd;">${item.product_name || ''}</td>
                <td style="padding: 5px; border: 1px solid #ddd; text-align: center;">${item.quantity || 0}</td>
                <td style="padding: 5px; border: 1px solid #ddd; text-align: right;">$${((item.selling_price || 0) * (item.quantity || 0)).toFixed(2)}</td>
              </tr>
        `
      })
      
      summary += `
            </tbody>
          </table>
          <hr>
          <p style="text-align: right;">
            <strong>Subtotal:</strong> $${(data.subtotal || 0).toFixed(2)}<br>
            <strong>Total:</strong> $${(data.total || 0).toFixed(2)}
          </p>
        </div>
      `
      
      return summary
    },
    
    deleteDraft(draft) {
      this.$bvModal.msgBoxConfirm(`Delete draft invoice ${draft.draft_id}?`, {
        title: 'Confirm Delete',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'DELETE',
        cancelTitle: 'Cancel',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      }).then(value => {
        if (value) {
          this.performDelete(draft.draft_id)
        }
      })
    },
    
    performDelete(draftId) {
      let path = '/api/sales/delete_draft_invoice/'
      let data = { draft_id: draftId }
      
      let that = this
      let s = function(response) {
        if (response.data.error) {
          that.$bvToast.toast(response.data.error, {
            title: 'Error',
            variant: 'warning'
          })
        } else {
          that.$bvToast.toast('Draft deleted successfully', {
            title: 'Success',
            variant: 'success'
          })
          that.loadDrafts()
        }
      }
      
      let f = function(error) {
        console.error('Error deleting draft:', error)
        that.$bvToast.toast('Error deleting draft', {
          title: 'Error',
          variant: 'danger'
        })
      }
      
      Requestor.postSyncP(path, data, this, s, f)
    },
    
    refresh() {
      this.pagination.page = 1
      this.loadDrafts()
    },
    
    viewEstimatedInvoice(draft) {
      this.selectedDraft = draft
      this.modalKey++
      this.loadPolicyText()
      this.$bvModal.show('modal-estimated-invoice-preview')
    },
    
    getAgreementData(draft) {
      if (!draft || !draft.data) return {}
      
      return {
        terms_agreed: draft.data.terms_agreed || false,
        warranty_refused: draft.data.warranty_refused || false,
        delivery_waiver_agreed: draft.data.delivery_waiver_agreed || false,
        space_preparation_agreed: draft.data.space_preparation_agreed || false,
        address_confirmation_agreed: draft.data.address_confirmation_agreed || false,
        has_signature: draft.data.has_signature || false,
        policy_language: draft.data.policy_language || 'en',
        policy_agreed_at: draft.data.policy_agreed_at || null,
        signature_captured_at: draft.data.signature_captured_at || null
      }
    },

    prepareInvoiceData(draft) {
      if (!draft) return {}
      
      const draftData = draft.data || {}
      
      return {
        estimate_id: draft.draft_id,
        draft_id: draft.draft_id,
        created_at: draft.created_at,
        created_by: draft.created_by || this.userDetails.username,
        location_id: draft.location_id,
        customer: draftData.customer || {},
        items: draftData.items || [],
        subtotal: draftData.subtotal || 0,
        delivery_charges: draftData.delivery_charges || 0,
        setup_charges: draftData.setup_charges || 0,
        warranty_charges: draftData.warranty_charges || 0,
        taxable_subtotal: draftData.taxable_subtotal || 0,
        tax_perc: draftData.tax_perc || 0.07,
        sales_tax: draftData.sales_tax || 0,
        total: draftData.total || 0,
        payment_methods: draftData.payment_methods || [],
        is_layaway: draftData.is_layaway || false,
        
        terms_agreed: draftData.terms_agreed || false,
        warranty_refused: draftData.warranty_refused || false,
        delivery_waiver_agreed: draftData.delivery_waiver_agreed || false,
        space_preparation_agreed: draftData.space_preparation_agreed || false,
        address_confirmation_agreed: draftData.address_confirmation_agreed || false,
        has_signature: draftData.has_signature || false,
        signature_url: draftData.signature_url || null,
        signature_captured_at: draftData.signature_captured_at || null
      }
    },
    
    loadPolicyText() {
      let path = "/api/policy/get_policy/"
      let data = {}
      
      let that = this
      let s = function(response) {
        if (response.data.result && response.data.result.length > 0) {
          that.policyText = response.data.result[0].text
        } else {
          that.policyText = '<p>Standard terms and conditions apply. Please contact store for complete details.</p>'
        }
      }
      
      let f = function(error) {
        console.error('Error loading policy:', error)
        that.policyText = '<p>Terms and conditions available at store.</p>'
      }
      
      Requestor.getAsyncP(path, data, this, s, f)
    },
    
    getLocationDetails(locationId) {
      if (!locationId) return {}
      
      if (this.locationDetailsCache[locationId]) {
        return this.locationDetailsCache[locationId]
      }
      
      try {
        const location = this.locationOptions.find(l => l.value == locationId)
        if (location) {
          const details = {
            address: location.address || '',
            email: location.email || '',
            phone: location.phone || '',
            short_name: location.short_name || '',
            website: 'www.myfurnituremecca.com'
          }
          
          this.$set(this.locationDetailsCache, locationId, details)
          return details
        }
      } catch (e) {
        console.error('Error getting location details:', e)
      }
      
      return {
        address: '',
        email: '',
        phone: '',
        short_name: '',
        website: 'www.myfurnituremecca.com'
      }
    },
    
    loadLocations() {
      if (localStorage.packet) {
        try {
          this.packet = JSON.parse(localStorage.packet)
          this.locationOptions = (this.packet.locations || []).map(loc => ({
            value: loc.value,
            text: loc.text,
            address: loc.address,
            email: loc.email,
            phone: loc.phone,
            short_name: loc.short_name
          }))
        } catch (e) {
          console.error('Error parsing packet:', e)
          this.locationOptions = []
        }
      }
    }
  },
  
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    this.loadLocations()
    this.loadDrafts()
    this.loadZipcodes()
    this.fetchPolicy()
  },
  
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
    if (this.loadingInterval) clearInterval(this.loadingInterval)
    if (this.msgInterval) clearInterval(this.msgInterval)
  }
}
</script>

<style scoped>
/* Glass Theme Background */
.glass-theme {
  position: relative;
  background: transparent !important;
  min-height: 100vh;
}

.glass-theme::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  filter: blur(10px);
  opacity: 0.5;
  z-index: -2;
  transform: scale(1.1);
}

/* Page Entrance Animation */
@keyframes fadeInPage {
  from {
    opacity: 0;
    transform: translateY(24px);
    filter: blur(2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
}

.fade-in-page {
  animation: fadeInPage 0.7s cubic-bezier(0.2, 0.9, 0.4, 1.1);
}

/* ========== ENHANCED LOADER ANIMATIONS WITH GLASS EFFECT ========== */
.custom-loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(12px);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeInOverlay 0.25s ease-out;
}

@keyframes fadeInOverlay {
  from { opacity: 0; backdrop-filter: blur(0px); }
  to { opacity: 1; backdrop-filter: blur(12px); }
}

.glass-loader-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.loader-card {
  padding: 40px 56px;
  text-align: center;
  animation: scaleIn 0.35s cubic-bezier(0.34, 1.2, 0.64, 1);
  min-width: 320px;
  min-height: 380px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

@keyframes scaleIn {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.glass-spinner-ring {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.glass-spinner-ring .ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-radius: 50%;
  animation: ringRotate 1.5s linear infinite;
}

.glass-spinner-ring .ring:nth-child(1) {
  border-top-color: #4299e1;
  border-left-color: #4299e1;
  animation-duration: 1s;
}

.glass-spinner-ring .ring:nth-child(2) {
  border-right-color: #48bb78;
  border-bottom-color: #48bb78;
  width: 80%;
  height: 80%;
  top: 10%;
  left: 10%;
  animation-duration: 1.2s;
  animation-direction: reverse;
}

.glass-spinner-ring .ring:nth-child(3) {
  border-left-color: #ed8936;
  border-bottom-color: #ed8936;
  width: 60%;
  height: 60%;
  top: 20%;
  left: 20%;
  animation-duration: 0.8s;
}

@keyframes ringRotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loader-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 20px 0;
}

.loader-dots span {
  width: 10px;
  height: 10px;
  background: linear-gradient(135deg, #2c7da0, #5fa8d3);
  border-radius: 50%;
  animation: bounce 1.2s infinite ease-in-out;
}

.loader-dots span:nth-child(1) { animation-delay: 0s; }
.loader-dots span:nth-child(2) { animation-delay: 0.15s; }
.loader-dots span:nth-child(3) { animation-delay: 0.3s; }
.loader-dots span:nth-child(4) { animation-delay: 0.45s; }

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1.2); opacity: 1; }
}

.loader-text {
  font-size: 14px;
  color: #2c7da0;
  font-weight: 500;
}

.loader-ellipsis {
  animation: ellipsis 1.5s infinite;
}

@keyframes ellipsis {
  0% { opacity: 0.3; }
  50% { opacity: 1; }
  100% { opacity: 0.3; }
}

.loading-progress {
  width: 80%;
  height: 4px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin: 0 auto;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4299e1, #48bb78, #ed8936, #4299e1);
  background-size: 200% 100%;
  animation: progressShine 1.5s linear infinite;
  transition: width 0.3s ease;
  border-radius: 4px;
}

@keyframes progressShine {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ========== FADE TRANSITIONS ========== */
.fade-loader-enter-active, .fade-loader-leave-active {
  transition: opacity 0.25s ease;
}
.fade-loader-enter, .fade-loader-leave-to {
  opacity: 0;
}

.fade-up-enter-active {
  animation: slideUp 0.4s ease-out;
}
.fade-up-leave-active {
  animation: slideDown 0.3s ease-in;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes slideDown {
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(20px); }
}

/* ========== GLASS STYLES ========== */
.glass-card {
  background: rgba(255, 255, 255, 0.85) !important;
  backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  border-radius: 20px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05) !important;
}

.glass-input {
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(5px) !important;
  border: 1px solid rgba(128, 128, 128, 0.3) !important;
  border-radius: 10px !important;
}

.glass-input:focus {
  border-color: rgba(54, 64, 80, 0.5) !important;
  box-shadow: 0 0 0 2px rgba(54, 64, 80, 0.1) !important;
}

.glass-select {
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(5px) !important;
  border: 1px solid rgba(128, 128, 128, 0.3) !important;
  border-radius: 10px !important;
}

.glass-btn {
  background: linear-gradient(135deg, rgba(54, 64, 80, 0.9), rgba(54, 64, 80, 0.95)) !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  transition: all 0.2s ease !important;
  border-radius: 10px !important;
}

.glass-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(54, 64, 80, 1), rgba(54, 64, 80, 1)) !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
}

.glass-badge {
  backdrop-filter: blur(4px) !important;
}

.glass-table {
  background: rgba(255, 255, 255, 0.7) !important;
  border-radius: 16px !important;
  overflow: hidden;
}

.glass-table th {
  background: rgba(0, 0, 0, 0.05) !important;
  color: #1e293b !important;
  font-weight: 600 !important;
}

.glass-table td {
  background: rgba(255, 255, 255, 0.5) !important;
}

.glass-table tr:hover td {
  background: rgba(255, 255, 255, 0.8) !important;
}

.glass-mobile-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  border-radius: 16px !important;
}

.glass-dropdown .dropdown-menu {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  border: 1px solid rgba(255, 255, 255, 0.5) !important;
  border-radius: 16px !important;
}

.glass-progress :deep(.progress-bar) {
  transition: width 0.6s cubic-bezier(0.2, 0.9, 0.4, 1.1);
}

.glass-pagination .page-item .page-link {
  background: rgba(255, 255, 255, 0.8) !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  color: #1e293b !important;
  border-radius: 8px !important;
  margin: 0 3px !important;
}

.glass-pagination .page-item.active .page-link {
  background: linear-gradient(135deg, #1e293b, #0f172a) !important;
  color: white !important;
  border-color: transparent !important;
}

/* ========== TABLE ROW ANIMATIONS ========== */
.animated-table tbody tr {
  transition: all 0.2s ease;
  animation: fadeInRow 0.3s ease-out forwards;
  opacity: 0;
}

@keyframes fadeInRow {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animated-table tbody tr:nth-child(1) { animation-delay: 0.02s; }
.animated-table tbody tr:nth-child(2) { animation-delay: 0.04s; }
.animated-table tbody tr:nth-child(3) { animation-delay: 0.06s; }
.animated-table tbody tr:nth-child(4) { animation-delay: 0.08s; }
.animated-table tbody tr:nth-child(5) { animation-delay: 0.1s; }

/* ========== HOVER EFFECTS ========== */
.animated-badge {
  transition: all 0.2s ease;
  cursor: default;
}
.animated-badge:hover {
  transform: translateY(-2px);
  filter: brightness(1.05);
}

.animated-dropdown-item {
  transition: all 0.15s ease;
}
.animated-dropdown-item:hover {
  transform: translateX(4px);
  background: rgba(0, 0, 0, 0.05);
}

.refresh-btn {
  transition: all 0.2s ease;
}
.refresh-btn:hover .refresh-icon {
  animation: spin 0.6s ease;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.action-toggle {
  transition: all 0.15s ease;
}
.action-toggle:hover {
  transform: scale(1.02);
}

/* ========== BADGE POP ANIMATION ========== */
.badge-pop-enter-active {
  animation: popIn 0.25s cubic-bezier(0.34, 1.2, 0.64, 1);
}

@keyframes popIn {
  from { transform: scale(0); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

/* ========== TITLE SECTION ========== */
.title-section {
  position: relative;
}
.title-underline {
  height: 3px;
  width: 60px;
  background: linear-gradient(90deg, #2c7da0, #a9d6e5);
  margin-top: 8px;
  border-radius: 3px;
  animation: expandWidth 0.5s ease-out;
}

@keyframes expandWidth {
  from { width: 0; opacity: 0; }
  to { width: 60px; opacity: 1; }
}

.count-badge {
  font-size: 14px;
  margin-left: 10px;
  vertical-align: middle;
  animation: pulseOnce 0.5s ease;
}

@keyframes pulseOnce {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
}

/* ========== RESPONSIVE CONTAINER ========== */
.responsive-container {
  padding: 15px;
}

@media (min-width: 768px) {
  .responsive-container {
    padding: 20px;
  }
}

@media (min-width: 1024px) {
  .responsive-container {
    padding: 20px 30px;
  }
}

.responsive-title {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #1e293b;
}

@media (max-width: 768px) {
  .responsive-title {
    font-size: 1.25rem;
  }
  .loader-card {
    padding: 24px 32px;
  }
}

/* Mobile Card Styles */
.mobile-card {
  transition: all 0.3s ease;
  animation: fadeSlideUp 0.35s ease-out;
}

@keyframes fadeSlideUp {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.mobile-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 24px rgba(0,0,0,0.1);
}

.mobile-card .card-header {
  background-color: rgba(0, 0, 0, 0.03);
  padding: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.mobile-card .draft-id {
  font-size: 14px;
  font-weight: 600;
  color: #0066cc;
}

.mobile-card .card-body {
  padding: 12px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  padding: 4px 0;
}

.info-row svg, .info-row i {
  flex-shrink: 0;
  margin-top: 2px;
  width: 18px;
  color: #64748b;
}

.info-label {
  font-weight: 600;
  color: #6c757d;
  min-width: 70px;
}

.info-value {
  color: #212529;
  word-break: break-word;
  flex: 1;
}

.customer-info {
  display: flex;
  gap: 8px;
  padding: 8px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  margin: 8px 0;
}

.customer-info svg, .customer-info i {
  flex-shrink: 0;
  margin-top: 2px;
  color: #64748b;
}

.customer-details {
  flex: 1;
}

.customer-details strong {
  font-size: 14px;
}

.agreement-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 8px 0;
}

.progress-section {
  margin-top: 8px;
}

.badge-group {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

/* Responsive Agreement Grid */
.responsive-agreement-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

@media (max-width: 768px) {
  .responsive-agreement-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
}

/* Touch-friendly button sizes */
@media (max-width: 768px) {
  .btn {
    min-height: 44px;
  }
  
  .btn-sm {
    min-height: 38px;
  }
  
  .dropdown-item {
    padding: 8px 16px;
  }
  
  .b-dropdown .dropdown-toggle {
    min-height: 38px;
  }
}

/* Smooth scrolling */
.responsive-container {
  scroll-behavior: smooth;
}

/* Modal animations */
.modal.fade .modal-dialog {
  transition: transform 0.25s ease-out, opacity 0.2s;
}

/* Pagination animations */
.animated-pagination .page-item .page-link {
  transition: all 0.15s ease;
}
.animated-pagination .page-item .page-link:hover {
  transform: translateY(-2px);
  background: #e9ecef;
}
</style>


// WEBPACK FOOTER //
// src/components/Sales/ListDraftInvoices.vue